import { Component, Input, OnInit } from '@angular/core';
import { BaseDynamicFormComponent } from 'src/app/common/components/dynamic-form-v2/components/base-dynamic-form/base-dynamic-form.component';
import { DynamicCardType } from 'src/app/common/components/dynamic-form-v2/enums/DynamicCardType';
import { DynamicInputType } from 'src/app/common/components/dynamic-form-v2/enums/DynamicInputType';
import { InputFieldType } from 'src/app/common/components/dynamic-form-v2/enums/InputFieldType';
import DynamicOptionsObject from 'src/app/common/components/dynamic-form-v2/dtos/DynamicOptionsObjectDTO';

@Component({
  selector: 'app-mystery-box-content-form',
  templateUrl: './mystery-box-content-form.component.html',
  styleUrls: ['./mystery-box-content-form.component.sass']
})
export class MysteryBoxContentFormComponent extends BaseDynamicFormComponent implements OnInit {

  mysteryBoxContentRecord: any = {};
  
  constructor() {
    super();
  }

  /**
   * Process the record when it's loaded to normalize lineItem format for Price component
   */
  onRecordLoaded(record: any) {
    if (!record) return;
    
    // Process lineItem field
    if (record.lineItem) {
      // Convert single lineItem object to array format expected by Price component
      if (!Array.isArray(record.lineItem)) {
        record.lineItem = [record.lineItem];
      }
      
      // Ensure each lineItem has the correct structure with populated t and id
      record.lineItem = record.lineItem.map((item: any) => {
        if (!item) return null;
        
        const processedItem: any = {
          c: item.c || 1,
          lineItemType: item.lineItemType || (item.t?.name || '')
        };
        
        // Process t (type/resource) - ensure it's an object with _id, id, name
        if (item.t) {
          if (typeof item.t === 'string') {
            // If it's just a string ObjectId, we can't populate it here
            // But we'll keep it as is and let the Price component handle it
            processedItem.t = { _id: item.t };
          } else if (item.t._id || item.t.name) {
            // Already an object, ensure it has the right structure
            processedItem.t = {
              _id: item.t._id || '',
              id: item.t.id || '',
              name: item.t.name || ''
            };
          } else {
            processedItem.t = item.t;
          }
        }
        
        // Process id (item/currency/etc) - ensure it's an object with _id, id, name
        if (item.id) {
          if (typeof item.id === 'string') {
            // If it's just a string ObjectId, we can't populate it here
            processedItem.id = { _id: item.id };
          } else if (item.id._id || item.id.name) {
            // Already an object, ensure it has the right structure
            processedItem.id = {
              _id: item.id._id || '',
              id: item.id.id || '',
              name: item.id.name || '',
              displayName: item.id.displayName || `${item.id.name || ''} (${item.id.id || ''})`
            };
          } else {
            processedItem.id = item.id;
          }
        }
        
        return processedItem;
      }).filter((item: any) => item !== null && (item.t || item.id));
    } else {
      // Initialize as empty array if not present
      record.lineItem = [];
    }
    
    // Update the record reference
    this.mysteryBoxContentRecord = record;
  }

  ngOnInit() {
    this.fields = [
      {
        title: "Mystery Box Content Data",
        fields: [
          {
            name: "Name",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.TextInput],
            clearField: true,
            key: 'name',
            isRequired: true,
            label: 'Name',
            columnWidth: 6
          },
          {
            name: "Description",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.InputTextArea],
            clearField: true,
            key: 'description',
            isRequired: false,
            label: 'Description',
            columnWidth: 6
          },
          {
            name: "Entry Type",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.Dropdown],
            clearField: true,
            key: 'entryType',
            isRequired: true,
            label: 'Entry Type',
            columnWidth: 6,
            options: {
              fieldName: 'entryType',
              values: [
                { label: 'Collection', value: 1 },
                { label: 'Item', value: 2 }
              ]
            } as DynamicOptionsObject,
            optionValue: 'value',
            optionLabel: 'label',
            displayControlType: "showHide",
            emitOnInit: true,
            displayControls: {
              ['lineItem']: (value: any) => value === 2,
              ['itemCollectionRef']: (value: any) => value === 1,
              ['coinWeight']: (value: any) => value === 1,
              ['diamondWeight']: (value: any) => value === 1,
            }
          },
          {
            name: "Weight",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.Number],
            clearField: true,
            key: 'weight',
            isRequired: true,
            label: 'Weight',
            columnWidth: 6
          },
          {
            name: "Line Item",
            inputType: DynamicInputType.Price,
            key: 'lineItem',
            options: { fieldName: '', version: 3 },
            columnWidth: 12,
            showField: false,
            label: 'Line Item'
          },
          {
            name: "Item Collection",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.AutoComplete],
            clearField: true,
            key: 'itemCollectionRef',
            label: 'Item Collection',
            columnWidth: 9,
            autocompleteMultipleValues: false,
            autocompleteShowEmptyMessage: true,
            autocompleteField: 'name',
            autocompleteMinLength: 0,
            suggestions: {
              fieldName: 'itemCollectionRef',
              apiController: 'collections',
              autopopulate: true,
              minimal: false,
              virtuals: false,
              customQueryField: 'name,id',
              select: '_id name id',
              isdisplayNameIDPath: false
            },
            style: { width: '100%' },
            showClear: true,
            showField: false
          },
          {
            name: 'Item Reward Quantity',
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.Number],
            clearField: true,
            key: 'itemRewardQuantity',
            isRequired: false,
            label: 'Item Reward Quantity',
            columnWidth: 3,
            style: { width: '100%' }
          },
          {
            name: "Coin Weight",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.AutoComplete],
            clearField: true,
            key: 'coinWeight',
            isRequired: false,
            label: 'Coin Weight',
            columnWidth: 6,
            autocompleteMultipleValues: false,
            autocompleteShowEmptyMessage: true,
            autocompleteField: 'name',
            autocompleteMinLength: 0,
            suggestions: {
              fieldName: 'coinWeight',
              apiController: 'nurture-rarity',
              autopopulate: true,
              minimal: false,
              virtuals: false,
              customQueryField: 'name,id',
              select: '_id name id displayName',
              isdisplayNameIDPath: false
            },
            style: { width: '100%' },
            showClear: true,
            showField: false
          },
          {
            name: "Diamond Weight",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.AutoComplete],
            clearField: true,
            key: 'diamondWeight',
            isRequired: false,
            label: 'Diamond Weight',
            columnWidth: 6,
            autocompleteMultipleValues: false,
            autocompleteShowEmptyMessage: true,
            autocompleteField: 'name',
            autocompleteMinLength: 0,
            suggestions: {
              fieldName: 'diamondWeight',
              apiController: 'nurture-rarity',
              autopopulate: true,
              minimal: false,
              virtuals: false,
              customQueryField: 'name,id',
              select: '_id name id displayName',
              isdisplayNameIDPath: false
            },
            style: { width: '100%' },
            showClear: true,
            showField: false
          }
        ],
        type: DynamicCardType.Card
      }
    ];
    this.title = "Mystery Box Content Form";
    this.viewRoute = 'mystery-box-content';
    this.isLoading = false;
  }
}

